var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getRectLabelPosition } from '../../utils/textLayout';
import { getPercentFromStringOrValue } from '../../utils/math';
import { Group, createRef, Path } from '../../element';
import { LegendType } from '../../interface/legend';
import * as lodash from '../../utils/lodash';
import { createRichText } from '../../rich';
import { SeriesModel } from '../../model';
var CandleStick = /** @class */ (function (_super) {
    __extends(CandleStick, _super);
    function CandleStick() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'candlestick';
        _this.legendType = LegendType.SERIES_LEGEND;
        return _this;
    }
    CandleStick.prototype.getDefaultOption = function () {
        return {
            type: 'candlestick',
            name: '',
            coord: 'cartesian2d',
            polar: 0,
            encode: {
                x: '',
                y: [''],
            },
            xAxis: 0,
            yAxis: 0,
            visible: true,
            showLegend: true,
            label: {
                show: false,
                position: 'outside',
                color: null,
                fontSize: 12,
                distance: 4,
            },
            barWidth: '72%',
            upColor: '#FF7762',
            downColor: '#0FB899',
            shapeStyle: {
                lineWidth: 1,
            },
        };
    };
    CandleStick.prototype.getInitialState = function () {
        return {
            points: [],
            barWidth: null,
            needAnimation: this.needAnimation(),
        };
    };
    CandleStick.prototype.beforeRender = function () {
        if (this.visible()) {
            this.state.points = this.getCandleItems();
        }
    };
    CandleStick.prototype.getCandleItems = function () {
        var _this = this;
        var _a = this.option, barWidth = _a.barWidth, upColor = _a.upColor, downColor = _a.downColor;
        var coord = this.getCoord();
        var categoryAxis = coord.getCategoryAxis();
        var valueAxis = coord.getValueAxis();
        var _b = categoryAxis.getState(), minCategory = _b.min, maxCategory = _b.max;
        var valueField = this.option.encode[valueAxis.shortType];
        var rangeData = this._dataset.pickFieldValues(valueField);
        var dataset = this.getDataset();
        var categoryAxisPixelValue = categoryAxis.getPixelValue();
        var barWidthValue = getPercentFromStringOrValue(barWidth, 1 / categoryAxisPixelValue);
        this.state.barWidth = Math.abs(barWidthValue / categoryAxisPixelValue);
        return rangeData.map(function (data, dataIndex) {
            var shapeRef = createRef();
            var labelRef = createRef();
            var openValue = data[0], closeValue = data[1], highValue = data[2], lowValue = data[3];
            var open = coord.dataToPoint({ x: dataIndex, y: openValue });
            var close = coord.dataToPoint({ x: dataIndex, y: closeValue });
            var high = coord.dataToPoint({ x: dataIndex, y: highValue });
            var low = coord.dataToPoint({ x: dataIndex, y: lowValue });
            var filtered = !(dataIndex >= minCategory && dataIndex <= maxCategory);
            var visible = !_this.invisibleItems.has(dataIndex) && !filtered;
            var isRise = closeValue >= openValue;
            var record = dataset.getRecord(dataIndex);
            var encode = _this._encode.getEncodeData(record, dataIndex);
            var name = encode[categoryAxis.shortType];
            var value = encode[valueAxis.shortType][1];
            var color = isRise ? upColor : downColor;
            var top = isRise ? close : open;
            return {
                encode: encode,
                name: name,
                value: value,
                color: color,
                plotX: top.x,
                plotY: top.y,
                open: open,
                close: close,
                high: high,
                low: low,
                isRise: isRise,
                visible: visible,
                filtered: filtered,
                shapeRef: shapeRef,
                labelRef: labelRef,
            };
        });
    };
    CandleStick.prototype.renderCandleItems = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('candle-item'),
            pointerEvents: 'none',
            onEvent: function (event) { return _this.handleSeriesUIEvent(event); },
        });
        var shapeStyle = this.option.shapeStyle;
        this.state.points.forEach(function (point, dataIndex) {
            if (!(_this.visible() && point.visible)) {
                return;
            }
            var plotX = point.plotX, plotY = point.plotY, open = point.open, close = point.close, high = point.high, low = point.low, isRise = point.isRise, color = point.color;
            var rectBottomCenter = isRise ? open : close;
            var rectStartX = plotX - _this.state.barWidth / 2;
            var barHeight = Math.abs(open.y - close.y);
            group.add(new Path(__assign({ key: dataIndex, brush: function (ctx) {
                    ctx.moveTo(rectStartX, plotY);
                    ctx.rect(rectStartX, plotY, _this.state.barWidth, barHeight);
                    ctx.moveTo(high.x, high.y);
                    ctx.lineTo(plotX, plotY);
                    ctx.moveTo(rectBottomCenter.x, rectBottomCenter.y);
                    ctx.lineTo(low.x, low.y);
                }, fill: color, stroke: color }, shapeStyle)));
        });
        return group;
    };
    CandleStick.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('candle-label'),
        });
        var _a = this.state, width = _a.barWidth, points = _a.points;
        var _b = this.option.label, show = _b.show, position = _b.position, distance = _b.distance, rich = _b.rich;
        var inside = position.indexOf('inside') !== -1;
        var textStyle = this.getTextStyle(this.option.label);
        points.forEach(function (point, dataIndex) {
            if (!(_this.visible() && point.visible)) {
                return;
            }
            var visible = point.visible, high = point.high, low = point.low, color = point.color;
            var textPosition = getRectLabelPosition({
                top: high.y,
                bottom: low.y,
                left: high.x - width / 2,
                right: high.x + width / 2,
            }, position, distance);
            var text = _this.getDataLabel(dataIndex);
            var label = createRichText(__assign(__assign(__assign({ key: dataIndex, ref: point.labelRef, display: show && _this.visible() && visible, rich: rich,
                text: text }, textPosition), { fill: inside ? '#ffffff' : '#6E748E', stroke: inside ? color : '#ffffff', lineWidth: 2 }), textStyle));
            group.add(label);
        });
        return group;
    };
    CandleStick.prototype.render = function () {
        var group = new Group({
            key: this.getKey('candlestick'),
        });
        var coord = this.getCoord();
        var valueAxis = coord.getValueAxis();
        var valueField = this.option.encode[valueAxis.shortType];
        if (lodash.isArray(valueField) && valueField.length >= 4) {
            group.add(this.renderCandleItems());
            if (this.option.label.show) {
                this.chart.addToTopLevel(this.renderLabels());
            }
        }
        else {
            throw new Error('The value axis field of CandleStick Chart should be an array and need be provided four fields.');
        }
        return group;
    };
    CandleStick.dependencies = ['coord', 'legend'];
    return CandleStick;
}(SeriesModel));
export default CandleStick;
